import React, { useState, useEffect, useRef } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { selectIsLoggedIn } from './auth/authSlice';
import { useTranslation } from 'react-i18next';
import './Sidebar.css';

import Logo from './assets/CityHunters-logo.png';
import UsaFlag from './assets/us-flag.png';
import BgFlag from './assets/bg-flag.png';

function Sidebar() {
    const [isOpen, setIsOpen] = useState(false);
    const [selectedLanguage, setSelectedLanguage] = useState(
        localStorage.getItem('selectedLanguage') || 'en'
    );
    const isLoggedIn = useSelector(selectIsLoggedIn);
    const { t, i18n } = useTranslation();
    const navigate = useNavigate();
    const sidebarRef = useRef(null);

    const changeLanguage = (lang) => {
        i18n.changeLanguage(lang);
        setSelectedLanguage(lang);
        localStorage.setItem('selectedLanguage', lang);
        window.location.reload();
    };

    const closeSidebar = () => setIsOpen(false);

    // Close sidebar on outside click
    useEffect(() => {
        const handleOutsideClick = (e) => {
            if (isOpen && sidebarRef.current && !sidebarRef.current.contains(e.target)) {
                // also don't close if clicking hamburger button
                if (!e.target.closest('.hamburger-btn')) {
                    closeSidebar();
                }
            }
        };
        document.addEventListener('mousedown', handleOutsideClick);
        return () => document.removeEventListener('mousedown', handleOutsideClick);
    }, [isOpen]);

    // Prevent body scroll when sidebar is open
    useEffect(() => {
        document.body.style.overflow = isOpen ? 'hidden' : '';
        return () => { document.body.style.overflow = ''; };
    }, [isOpen]);

    const navItems = [
        { to: '/', label: t('home'), icon: '🏠' },
        { to: '/games', label: t('games'), icon: '🗺️' },
        { to: '/start_game', label: t('start_a_game'), icon: '🎮' },
        { to: '/contacts', label: t('contacts'), icon: '📬' },
        ...(isLoggedIn
            ? [{ to: '/mygames', label: t('my_games'), icon: '🏆' }]
            : [{ to: '/login', label: t('login'), icon: '🔑' }]
        ),
    ];

    return (
        <>
            {/* ── TOP BAR ── */}
            <header className="topbar">
                {/* Hamburger */}
                <button
                    className="hamburger-btn"
                    onClick={() => setIsOpen((prev) => !prev)}
                    aria-label="Open menu"
                >
                    <span className={`bar ${isOpen ? 'open' : ''}`} />
                    <span className={`bar ${isOpen ? 'open' : ''}`} />
                    <span className={`bar ${isOpen ? 'open' : ''}`} />
                </button>

                {/* Logo — center */}
                <div className="topbar-logo" onClick={() => navigate('/')}>
                    <img src={Logo} alt="City Hunters" />
                </div>

                {/* Language flags — right */}
                <div className="topbar-lang">
                    <img
                        src={UsaFlag}
                        alt="English"
                        className={`flag-icon ${selectedLanguage === 'en' ? 'flag-active' : ''}`}
                        onClick={() => changeLanguage('en')}
                    />
                    <img
                        src={BgFlag}
                        alt="Bulgarian"
                        className={`flag-icon ${selectedLanguage === 'bg' ? 'flag-active' : ''}`}
                        onClick={() => changeLanguage('bg')}
                    />
                </div>
            </header>

            {/* ── OVERLAY ── */}
            <div
                className={`sidebar-overlay ${isOpen ? 'overlay-visible' : ''}`}
                onClick={closeSidebar}
            />

            {/* ── SIDEBAR PANEL ── */}
            <nav ref={sidebarRef} className={`sidebar ${isOpen ? 'sidebar-open' : ''}`}>
                {/* Sidebar header */}
                <div className="sidebar-header">
                    <img src={Logo} alt="City Hunters" className="sidebar-logo" />
                    <p className="sidebar-tagline"> Explore · Discover · Conquer</p>
                </div>

                <div className="sidebar-divider" />

                {/* Nav links */}
                <ul className="sidebar-nav">
                    {navItems.map((item) => (
                        <li key={item.to}>
                            <NavLink
                                to={item.to}
                                className={({ isActive }) =>
                                    `sidebar-link ${isActive ? 'sidebar-link-active' : ''}`
                                }
                                onClick={closeSidebar}
                                end={item.to === '/'}
                            >
                                <span className="sidebar-icon">{item.icon}</span>
                                <span className="sidebar-label">{item.label}</span>
                            </NavLink>
                        </li>
                    ))}
                </ul>

                <div className="sidebar-divider" />

                {/* Bottom language switcher inside sidebar too */}
                <div className="sidebar-footer">
                    <p className="sidebar-footer-title">Language</p>
                    <div className="sidebar-flags">
                        <button
                            className={`flag-btn ${selectedLanguage === 'en' ? 'flag-btn-active' : ''}`}
                            onClick={() => changeLanguage('en')}
                        >
                            <img src={UsaFlag} alt="EN" /> EN
                        </button>
                        <button
                            className={`flag-btn ${selectedLanguage === 'bg' ? 'flag-btn-active' : ''}`}
                            onClick={() => changeLanguage('bg')}
                        >
                            <img src={BgFlag} alt="BG" /> BG
                        </button>
                    </div>
                </div>
            </nav>
        </>
    );
}

export default Sidebar;
